import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_TEL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_WHATSAPP_URL
} from '../config/contact';
import { SPRING_DEFAULT, REDUCED_MOTION_TRANSITION } from '../utils/springs';
import './Contact.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const { t } = useLanguage();
  const [formStatus, setFormStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fallbackHref, setFallbackHref] = useState(null);
  const [fallbackLabel, setFallbackLabel] = useState('');
  const reduceMotion = useReducedMotion();
  const messageTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;

  const validate = (form) => {
    const name = form.elements.name.value.trim();
    const email = form.elements.email.value.trim();
    const message = form.elements.message.value.trim();
    const errors = {};

    if (!name) {
      errors.name = t('contactPage.nameRequired');
    }
    if (!email) {
      errors.email = t('contactPage.emailRequired');
    } else if (!EMAIL_PATTERN.test(email)) {
      errors.email = t('contactPage.emailInvalid');
    }
    if (!message) {
      errors.message = t('contactPage.messageRequired');
    }

    return { errors, name, email, message, valid: Object.keys(errors).length === 0 };
  };

  const openWhatsApp = (href) => {
    setFallbackHref(null);
    const popup = window.open(href, '_blank');
    if (!popup) {
      setFormStatus('blocked');
      setFallbackHref(href);
      setFallbackLabel(t('contactPage.whatsappLinkLabel'));
      return;
    }
    popup.opener = null;
    setFormStatus('whatsapp');
  };

  const openEmail = (href) => {
    setFallbackHref(null);
    window.location.href = href;
    setFormStatus('email');
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    const { errors, name, email, message, valid } = validate(e.currentTarget);
    setFieldErrors(errors);
    if (!valid) {
      setFormStatus(null);
      return;
    }

    const body = `${t('contactPage.getInTouch')}\n\n${t('contactPage.nameLabel')}: ${name}\n${t('contactPage.emailLabel')}: ${email}\n\n${message}`;
    const href = `${CONTACT_WHATSAPP_URL}?text=${encodeURIComponent(body)}`;
    openWhatsApp(href);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const form = e.target.closest('form');
    const { errors, name, email, message, valid } = validate(form);
    setFieldErrors(errors);
    if (!valid) {
      setFormStatus(null);
      return;
    }

    const subject = encodeURIComponent(`${t('contactPage.title')} — deezNuts`);
    const body = encodeURIComponent(
      `${t('contactPage.nameLabel')}: ${name}\n${t('contactPage.emailLabel')}: ${email}\n\n${message}`
    );
    const href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    openEmail(href);
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <h1>{t('contactPage.title')}</h1>
      </section>

      <div className="container">
        <div className="contact-container">
          <div className="contact-grid">
            <div className="contact-info-section">
              <h2>{t('contactPage.getInTouch')}</h2>

              <div className="info-block">
                <div className="info-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v16H4z" />
                    <path d="M4 9h16M9 4v16" />
                  </svg>
                </div>
                <div className="info-content">
                  <h3>{t('contactPage.email')}</h3>
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </div>
              </div>

              <div className="info-block">
                <div className="info-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div className="info-content">
                  <h3>{t('contactPage.phoneLabel')}</h3>
                  <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE_DISPLAY}</a>
                </div>
              </div>

              <div className="info-block">
                <div className="info-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="info-content">
                  <h3>{t('contactPage.orchard')}</h3>
                  <p>{t('contactPage.location')}</p>
                </div>
              </div>
            </div>

            <form className="contact-form contact-form-card" onSubmit={handleWhatsAppSubmit} noValidate>
              <AnimatePresence>
                {formStatus === 'whatsapp' && (
                  <motion.p
                    className="form-message form-message--success"
                    role="status"
                    initial={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    animate={
                      reduceMotion
                        ? { opacity: 1 }
                        : { opacity: 1, scale: 1 }
                    }
                    exit={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    transition={messageTransition}
                  >
                    {t('contactPage.openedWhatsApp')}
                  </motion.p>
                )}
                {formStatus === 'email' && (
                  <motion.p
                    className="form-message form-message--success"
                    role="status"
                    initial={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    animate={
                      reduceMotion
                        ? { opacity: 1 }
                        : { opacity: 1, scale: 1 }
                    }
                    exit={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    transition={messageTransition}
                  >
                    {t('contactPage.openedEmail')}
                  </motion.p>
                )}
                {formStatus === 'blocked' && fallbackHref && (
                  <motion.p
                    className="form-message form-message--blocked"
                    role="status"
                    initial={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    animate={
                      reduceMotion
                        ? { opacity: 1 }
                        : { opacity: 1, scale: 1 }
                    }
                    exit={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.96 }
                    }
                    transition={messageTransition}
                  >
                    {t('contactPage.popupBlocked')}{' '}
                    <a href={fallbackHref} target="_blank" rel="noopener noreferrer">
                      {fallbackLabel}
                    </a>
                  </motion.p>
                )}
              </AnimatePresence>

              <div className="form-group">
                <label htmlFor="name">{t('contactPage.nameLabel')}</label>
                <input type="text" id="name" name="name" aria-invalid={fieldErrors.name ? 'true' : undefined} />
                {fieldErrors.name ? (
                  <p className="field-error" role="alert">
                    {fieldErrors.name}
                  </p>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('contactPage.emailLabel')}</label>
                <input type="email" id="email" name="email" aria-invalid={fieldErrors.email ? 'true' : undefined} />
                {fieldErrors.email ? (
                  <p className="field-error" role="alert">
                    {fieldErrors.email}
                  </p>
                ) : null}
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('contactPage.messageLabel')}</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  aria-invalid={fieldErrors.message ? 'true' : undefined}
                />
                {fieldErrors.message ? (
                  <p className="field-error" role="alert">
                    {fieldErrors.message}
                  </p>
                ) : null}
              </div>

              <button type="submit" className="submit-btn">
                {t('contactPage.sendButton')}
              </button>
              <button type="button" className="submit-btn submit-btn--secondary" onClick={handleEmailSubmit}>
                {t('contactPage.sendEmail')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
